import React from 'react';

import { IconButton } from '@mui/material';
import { Trash2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import {
  useAreWorkflowStepsEditable,
  useCheckWorkflowAuthorization,
  useDeleteActions,
} from 'src/hooks';
import type { EnvelopeRecipientActionWithStep } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles()(theme => ({
  deleteButton: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 8,
    height: theme.appSizes.inputHeight,
    width: theme.appSizes.inputHeight,
  },
  hiddenButton: {
    opacity: 0,
    pointerEvents: 'none',
  },
}));

interface DeleteActionButtonProps {
  action: EnvelopeRecipientActionWithStep;
  workflowId: UUID;
}

export function DeleteActionButton({
  action,
  workflowId,
}: DeleteActionButtonProps): JSX.Element | null {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { deleteActions } = useDeleteActions();
  const { areStepsEditable } = useAreWorkflowStepsEditable(workflowId);
  const { canEditWorkflow } = useCheckWorkflowAuthorization(workflowId);

  async function deleteWorkflowActions(): Promise<void> {
    const { data } = await deleteActions({
      variables: { input: { actionIds: [action.id] } },
    });
    if (data)
      enqueueSnackbar('Deleted actions from workflow.', {
        key: uuid4(),
        variant: 'success',
      });
  }

  if (!areStepsEditable) return null;

  return (
    <IconButton
      className={classes.deleteButton}
      data-cy="delete-workflow-actions"
      disabled={!canEditWorkflow}
      size="small"
      onClick={deleteWorkflowActions}
    >
      <Trash2 size={18} />
    </IconButton>
  );
}

export function NewDeleteActionButton({ onClick }: { onClick: () => void }): JSX.Element | null {
  const { classes } = useStyles();
  return (
    <IconButton className={classes.deleteButton} size="small" onClick={onClick}>
      <Trash2 size={18} />
    </IconButton>
  );
}
